"use client";

import React, { useState } from "react";
import { PhoneCall, Mail, MessageSquare, Copy, Check, Loader } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
const inputClasses = "flex h-10 w-full rounded-sm border border-border bg-secondary/50 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono";
const textareaClasses = "flex min-h-[120px] w-full rounded-sm border border-border bg-secondary/50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono";
function SupportForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary/5 border border-primary/20 rounded-sm text-center space-y-4 backdrop-blur-md">
                <div className="mx-auto size-12 bg-primary/20 rounded-sm flex items-center justify-center mb-4">
                    <Check className="size-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold font-default text-foreground uppercase tracking-tighter">Strategy Brief Received</h3>
                <p className="text-muted-foreground font-default">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="mt-4 rounded-sm border-primary text-primary hover:bg-primary hover:text-white">
                    Submit Another Brief
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default uppercase tracking-widest text-xs font-bold">Consultant Name</FormLabel>
                            <FormControl>
                                <input type="text" placeholder="ALEX BENCHMARK" className={inputClasses} {...field} />
                            </FormControl>
                            <p className="text-[0.7rem] text-muted-foreground font-mono uppercase">Primary point of contact for the account</p>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default uppercase tracking-widest text-xs font-bold">Direct Line</FormLabel>
                            <FormControl>
                                <input type="tel" placeholder="+1 (555) 000-0000" className={inputClasses} {...field} />
                            </FormControl>
                            <p className="text-[0.7rem] text-muted-foreground font-mono uppercase">For immediate strategic coordination</p>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default uppercase tracking-widest text-xs font-bold">Corporate Email</FormLabel>
                            <FormControl>
                                <input type="email" placeholder="strategy@benchmark.media" className={inputClasses} {...field} />
                            </FormControl>
                            <p className="text-[0.7rem] text-muted-foreground font-mono uppercase">Secure channel for data transmission</p>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="vehicle_reg" rules={{
        required: "Company name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default uppercase tracking-widest text-xs font-bold">Organization</FormLabel>
                            <FormControl>
                                <input type="text" placeholder="ENTERPRISE CORP" className={inputClasses} {...field} />
                            </FormControl>
                            <p className="text-[0.7rem] text-muted-foreground font-mono uppercase">Legal entity seeking consultation</p>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="location" rules={{
        required: "Market focus is required"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
                            <FormLabel className="font-default uppercase tracking-widest text-xs font-bold">Market Focus</FormLabel>
                            <FormControl>
                                <input type="text" placeholder="Global / North America / EMEA" className={inputClasses} {...field} />
                            </FormControl>
                            <p className="text-[0.7rem] text-muted-foreground font-mono uppercase">Primary geographic or demographic target</p>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="issue" rules={{
        required: "Please describe the objectives"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
                            <FormLabel className="font-default uppercase tracking-widest text-xs font-bold">Strategic Objectives</FormLabel>
                            <FormControl>
                                <textarea placeholder="Outline your brand positioning and growth targets..." className={textareaClasses} {...field} />
                            </FormControl>
                            <p className="text-[0.7rem] text-muted-foreground font-mono uppercase">Define the scope of the advertising challenge</p>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="preferred_contact" rules={{
        required: "Please select a preferred contact method"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2 space-y-3">
                            <FormLabel className="font-default uppercase tracking-widest text-xs font-bold">Communication Protocol</FormLabel>
                            <FormControl>
                                <div className="flex gap-6">
                                    {["Phone", "Email"].map((option, i) => <label key={option} data-index={i} className="flex items-center space-x-2 cursor-pointer">
                                            <input type="radio" value={option} checked={field.value === option} onChange={field.onChange} className="size-4 border-primary text-primary focus:ring-primary bg-background" />
                                            <span className="text-sm font-default font-medium leading-none uppercase tracking-tight">{option}</span>
                                        </label>)}
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            {submitError && <div className="mt-6 p-4 bg-destructive/10 border border-destructive/20 rounded-sm">
                    <p className="text-destructive text-xs font-mono font-medium uppercase">{submitError}</p>
                </div>}

            <div className="mt-8">
                <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto min-w-[240px] rounded-sm bg-primary text-white font-default font-bold uppercase tracking-widest shadow-[0_0_15px_rgba(45,91,255,0.3)] hover:shadow-[0_0_25px_rgba(45,91,255,0.5)] transition-all">
                    {isSubmitting ? <>
                            <Loader className="animate-spin mr-2 size-5" />
                            Processing Data...
                        </> : 'Initiate Consultation'}
                </Button>
            </div>
        </Form>;
}
export default function DirectSupport() {
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const phone = "+61485040630";
  const email = "info@benchmarkmediaconsulting.com";
  const handleCopy = (text: string, type: 'phone' | 'email') => {
    navigator.clipboard.writeText(text);
    if (type === 'phone') {
      setCopiedPhone(true);
      setTimeout(() => setCopiedPhone(false), 2000);
    } else {
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 2000);
    }
  };
  return <section data-section-id="246984" id="direct-line-to-strategy" className="dark bg-background text-foreground py-24 px-4 md:px-8 lg:px-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none"></div>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-primary/10 blur-[160px] rounded-full pointer-events-none"></div>

            <div className="max-w-7xl mx-auto relative z-10">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <h2 className="text-4xl md:text-6xl font-black font-default tracking-tighter mb-4 uppercase italic">
                        Direct Line to Strategy
                    </h2>
                    <p className="text-lg text-muted-foreground font-default max-w-2xl mx-auto">
                        Connect with Benchmark Media Consulting for elite advertising solutions. Our executive team is ready to architect your brand's market dominance.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
                    <Card className="border-primary/30 bg-white/5 backdrop-blur-md shadow-2xl shadow-primary/5 relative overflow-hidden group rounded-sm">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                        <CardHeader className="text-center pb-2">
                            <div className="mx-auto bg-primary/10 p-4 rounded-sm mb-4 border border-primary/20">
                                <PhoneCall className="size-10 text-primary" />
                            </div>
                            <CardTitle className="text-xl font-default uppercase tracking-widest font-black">Executive Line</CardTitle>
                            <CardDescription className="text-sm font-mono uppercase tracking-tight">Priority Strategic Access</CardDescription>
                        </CardHeader>
                        <CardContent className="text-center pb-6">
                            <p className="text-xl font-mono font-bold text-foreground tracking-tighter">{phone}</p>
                        </CardContent>
                        <CardFooter className="justify-center pt-0">
                            <Button variant="default" className="w-full rounded-sm bg-primary hover:bg-primary/90 font-default font-bold uppercase tracking-widest text-xs" onClick={() => handleCopy(phone, 'phone')}>
                                {copiedPhone ? <Check className="mr-2 size-4" /> : <Copy className="mr-2 size-4" />}
                                {copiedPhone ? 'SECURED' : 'COPY LINE'}
                            </Button>
                        </CardFooter>
                    </Card>

                    <Card className="border-white/10 bg-white/5 backdrop-blur-md hover:border-primary/40 transition-all duration-500 rounded-sm">
                        <CardHeader className="text-center pb-2">
                            <div className="mx-auto bg-secondary p-4 rounded-sm mb-4 border border-white/5">
                                <Mail className="size-10 text-primary" />
                            </div>
                            <CardTitle className="text-xl font-default uppercase tracking-widest font-black">Brief Submission</CardTitle>
                            <CardDescription className="text-sm font-mono uppercase tracking-tight">Proposals & Data Sets</CardDescription>
                        </CardHeader>
                        <CardContent className="text-center pb-6">
                            <p className="text-lg font-mono font-medium text-foreground truncate px-2">{email}</p>
                        </CardContent>
                        <CardFooter className="justify-center pt-0">
                            <Button variant="outline" className="w-full rounded-sm border-white/10 hover:bg-white/5 font-default font-bold uppercase tracking-widest text-xs" onClick={() => handleCopy(email, 'email')}>
                                {copiedEmail ? <Check className="mr-2 size-4" /> : <Copy className="mr-2 size-4" />}
                                {copiedEmail ? 'SECURED' : 'COPY EMAIL'}
                            </Button>
                        </CardFooter>
                    </Card>
                </div>

                <div className="max-w-4xl mx-auto bg-card/30 border border-white/10 rounded-sm p-6 md:p-10 shadow-4xl backdrop-blur-xl">
                    <div className="mb-8 border-l-4 border-primary pl-6">
                        <h3 className="text-2xl font-black font-default uppercase tracking-tighter mb-2">Strategic Briefing Form</h3>
                        <p className="text-muted-foreground font-default">Provide the foundational data for your marketing transformation. Our analysts will review and respond within 24 hours.</p>
                    </div>
                    
                    <FormProvider formKey="strategy_request_form_connect" formId="strategy_request_form_connect" sectionName="Direct Support" defaultValues={{
          name: "",
          phone: "",
          email: "",
          vehicle_reg: "",
          location: "",
          issue: "",
          preferred_contact: "Phone"
        }} validationRules={{
          name: {
            required: true
          },
          phone: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          vehicle_reg: {
            required: true
          },
          location: {
            required: true
          },
          issue: {
            required: true
          },
          preferred_contact: {
            required: true
          }
        }} submitText="Initiate Consultation" successMessage="Your strategic brief has been successfully transmitted to our executive team.">
                        <SupportForm />
                    </FormProvider>
                </div>
            </div>
        </section>;
}